// ─── src/lib/api.js ───────────────────────────────────────────────────────────
//
// DATA LAYER — currently uses localStorage.
//
// ⚡ TO UPGRADE TO SUPABASE (production):
//   1. npm install @supabase/supabase-js
//   2. Create a `supabase_content` table with a single row (id=1, data=jsonb)
//   3. Replace getContent() and saveContent() below with Supabase calls:
//
//   import { createClient } from '@supabase/supabase-js';
//   const supabase = createClient(import.meta.env.VITE_SUPABASE_URL, import.meta.env.VITE_SUPABASE_ANON_KEY);
//
//   export const getContent = async () => {
//     const { data } = await supabase.from('content').select('data').eq('id', 1).single();
//     return data?.data ?? null;
//   };
//
//   export const saveContent = async (content) => {
//     await supabase.from('content').upsert({ id: 1, data: content });
//   };
//
// ─────────────────────────────────────────────────────────────────────────────

import { SITE_DEFAULTS } from '../data/content';

const STORAGE_KEY = 'brocode_cms_content';

/**
 * Detect and migrate old-format team member to the current format.
 * Old format used: { name, title, image, bio, specialties, location, instagram }
 * New format uses: { name, role,  img,   bio, imgPosition, category }
 */
const migrateTeamMember = (m) => {
  // Already new format — has 'img' field
  if ('img' in m) return m;
  // Old format — map across
  const titleLower = (m.title || '').toLowerCase();
  let category = 'barber';
  if (titleLower.includes('founder') || titleLower.includes('owner') || titleLower.includes('co-owner')) {
    category = 'leadership';
  } else if (titleLower.includes('tattoo')) {
    category = 'tattoo';
  }
  return {
    id:          m.id || Date.now(),
    name:        (m.name || '').toUpperCase(),
    role:        (m.title || 'BARBER').toUpperCase(),
    img:         m.image || '',
    bio:         m.bio || '',
    imgPosition: 'center 50%',
    category,
  };
};

export const getContent = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return SITE_DEFAULTS;
    const parsed = JSON.parse(stored);

    // If stored team is in the OLD format (has 'image'/'title' but not 'img'/'role'),
    // it's the old placeholder barbers (Marcus V., Jordan K., etc.) — discard it
    // so SITE_DEFAULTS.team (the real crew) is used instead.
    if (Array.isArray(parsed.team) && parsed.team.length > 0) {
      const first = parsed.team[0];
      if ('image' in first && !('img' in first)) {
        delete parsed.team; // fall back to SITE_DEFAULTS.team
      } else {
        // Saved rosters must not restore barbers who have left.
        parsed.team = parsed.team.filter(member => !['AK', 'LOVE', 'MANA'].includes(member.name?.trim().toUpperCase()));
        if (!parsed.teamRosterVersion) {
          // Upgrade older saved rosters; later admin removals remain intentional.
          const additions = SITE_DEFAULTS.team.filter(member => ['NITIN', 'BAL'].includes(member.name));
          parsed.team.push(...additions.filter(member => !parsed.team.some(saved => saved.name?.trim().toUpperCase() === member.name)));
          parsed.teamRosterVersion = SITE_DEFAULTS.teamRosterVersion;
        }
        parsed.team = parsed.team.map(member =>
          ['NITIN', 'BAL'].includes(member.name?.trim().toUpperCase()) && member.bio === 'Barber at Bro Code'
            ? { ...member, bio: '' }
            : member);
      }
    }

    return { ...SITE_DEFAULTS, ...parsed };
  } catch {
    return SITE_DEFAULTS;
  }
};

export const saveContent = (content) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(content));
    // Dispatch storage event so other open tabs (e.g. /team) pick up the change
    window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));
    return true;
  } catch {
    return false;
  }
};

export const resetContent = () => {
  localStorage.removeItem(STORAGE_KEY);
  return SITE_DEFAULTS;
};

// ─── Admin auth ───────────────────────────────────────────────────────────────
// Password is stored as a SHA-256 hash in .env.local (REACT_APP_ADMIN_HASH).
// The hash is embedded in the CRA bundle at build time, but the plaintext
// password is never in source code or git history.
//
// To change the password:
//   echo -n "YourNewPassword" | shasum -a 256
//   → paste the result into .env.local and restart the dev server.
//
// TO UPGRADE: Replace with Supabase Auth (supabase.auth.signInWithPassword)
// ─────────────────────────────────────────────────────────────────────────────

const MAX_ATTEMPTS  = 3;
const LOCK_KEY      = 'brocode_locked';
const FAILS_KEY     = 'brocode_login_fails';

async function sha256hex(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export const isAdminLocked = () => localStorage.getItem(LOCK_KEY) === '1';

/** Returns { success, locked, remaining } */
export const adminLogin = async (password) => {
  if (isAdminLocked()) return { success: false, locked: true, remaining: 0 };

  const hash     = await sha256hex(password);
  const expected = process.env.REACT_APP_ADMIN_HASH;

  if (hash === expected) {
    sessionStorage.setItem('brocode_admin', 'true');
    localStorage.removeItem(FAILS_KEY);
    return { success: true };
  }

  // Failed — increment counter
  const fails = parseInt(localStorage.getItem(FAILS_KEY) || '0', 10) + 1;
  if (fails >= MAX_ATTEMPTS) {
    localStorage.setItem(LOCK_KEY, '1');
    localStorage.removeItem(FAILS_KEY);
    return { success: false, locked: true, remaining: 0 };
  }
  localStorage.setItem(FAILS_KEY, String(fails));
  return { success: false, locked: false, remaining: MAX_ATTEMPTS - fails };
};

export const adminLogout = () => {
  sessionStorage.removeItem('brocode_admin');
};

export const isAdminLoggedIn = () => {
  return sessionStorage.getItem('brocode_admin') === 'true';
};
